import { useState } from 'react';
import { S } from '../regureStyles';

// Case Volume.
//
// A hand-rolled SVG rather than a charting library, so the hover/focus
// affordance is built here too. The plot itself uses
// preserveAspectRatio='none', which stretches SVG user units unevenly and
// makes SVG-space hit-testing unreliable; the interaction layer is therefore
// plain HTML sitting on top, one equal-width region per point. That also gives
// the chart keyboard access for free — each region is a tabbable button, so a
// keyboard user can read every month's figure without a pointer.
//
// The tooltip reports only what the series already contains. It never
// recomputes, re-buckets or re-labels the data.
const AreaChart = ({ data, color = S.burgundy, describePoint }) => {
  const W = 100;
  const H = 60;
  const [active, setActive] = useState(null);

  // Only a genuinely empty series gets the bare baseline. The guard used to
  // be `data.length < 2`, which silently swallowed a ONE-point series: a
  // tenant whose cases all landed in a single month got an empty plot area
  // sitting directly above correct AVG/PEAK/YTD figures, which reads as a
  // broken chart rather than as "one month of data".
  if (!data || data.length === 0) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio='none'>
        <line x1='0' y1={H} x2={W} y2={H} stroke={S.borderLight} strokeWidth='1' />
      </svg>
    );
  }
  // A single month has no interval to draw a slope across, so it is plotted
  // as a plateau spanning the full width — the value is legible and the
  // filled area still communicates magnitude. Duplicating the point keeps
  // the interpolation below identical for every series length.
  const series = data.length === 1 ? [data[0], data[0]] : data;
  const max = Math.max(...series.map(d => d.value), 1);
  const pts = series.map((d, i) => {
    const x = (i / (series.length - 1)) * W;
    const y = H - (d.value / max) * H * 0.85;
    return `${x},${y}`;
  });
  const polyline = pts.join(' ');
  const fillPath = `M${pts[0]} ${pts.join(' L')} L${W},${H} L0,${H} Z`;

  const activePoint = active == null ? null : data[active];
  const label = activePoint && describePoint ? describePoint(activePoint) : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%', display: 'block' }} preserveAspectRatio='none'>
        <defs>
          <linearGradient id='areaGrad' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={color} stopOpacity='0.18' />
            <stop offset='100%' stopColor={color} stopOpacity='0.01' />
          </linearGradient>
        </defs>
        <path d={fillPath} fill='url(#areaGrad)' />
        <polyline points={polyline} fill='none' stroke={color} strokeWidth='1.5' strokeLinejoin='round' />
        {active != null && data.length > 1 && (
          <line
            x1={(active / (data.length - 1)) * W}
            y1='0'
            x2={(active / (data.length - 1)) * W}
            y2={H}
            stroke={color}
            strokeWidth='0.5'
            strokeOpacity='0.5'
          />
        )}
      </svg>

      {/* Interaction layer: one equal-width region per data point. */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
        {data.map((d, i) => (
          <button
            key={`${d.year ?? ''}-${d.month}`}
            type='button'
            aria-label={describePoint ? describePoint(d) : `${d.month}: ${d.value}`}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(a => (a === i ? null : a))}
            onFocus={() => setActive(i)}
            onBlur={() => setActive(a => (a === i ? null : a))}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: 'default',
              // The visible cue is the tooltip and the guide line; the region
              // itself stays invisible so it cannot alter the plot's reading.
              outline: 'none',
            }}
          />
        ))}
      </div>

      {label && (
        <div
          role='status'
          style={{
            position: 'absolute',
            left: `${(active / Math.max(data.length - 1, 1)) * 100}%`,
            bottom: '100%',
            transform: `translateX(${active === 0 ? '0' : active === data.length - 1 ? '-100%' : '-50%'})`,
            marginBottom: 6,
            background: S.textPrimary,
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 9px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            zIndex: 2,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

export default AreaChart;
